import { useCallback, useEffect, useMemo, useState } from 'react';
import type { UserIncomeReportRowType } from '@econtractor/types';
import * as apiReport from 'api/apiReports';
import { useToast } from 'context/ToastContext';
import { useTranslation } from 'react-i18next';
import type { ReportDateFilterState } from '../../helper/reportObjectsStorage';
import {
    loadReportUsersFilters,
    saveReportUsersFilters,
    type ReportUsersObjectFilter,
    type ReportUsersViewMode,
} from '../../helper/reportUsersStorage';
import { filterUsersByObject } from './filterByObject';

export const useReportUsers = () => {
    const { t } = useTranslation(['Reports']);
    const { showToast } = useToast();
    const savedFilters = useMemo(() => loadReportUsersFilters(), []);

    const [users, setUsers] = useState<UserIncomeReportRowType[] | null>(null);
    const [isReporting, setIsReporting] = useState(false);
    const [dates, setDates] = useState<[number, number] | null>(savedFilters.dates);
    const [viewMode, setViewMode] = useState<ReportUsersViewMode>(savedFilters.viewMode);
    const [objectFilter, setObjectFilter] = useState<ReportUsersObjectFilter>(savedFilters.objectFilter);
    const [dateFilter, setDateFilter] = useState<ReportDateFilterState>({
        period: savedFilters.period,
        dates: savedFilters.dates,
        customRange: savedFilters.customRange,
    });

    const fromMs = dates?.[0] ?? null;
    const toMs = dates?.[1] ?? null;

    const persist = useCallback(
        (patch: Partial<{
            dateFilter: ReportDateFilterState;
            viewMode: ReportUsersViewMode;
            objectFilter: ReportUsersObjectFilter;
        }>) => {
            const nextFilter = patch.dateFilter ?? dateFilter;
            const nextView = patch.viewMode ?? viewMode;
            const nextObjectFilter = patch.objectFilter ?? objectFilter;
            if (!nextFilter.dates?.[0] || !nextFilter.dates?.[1]) return;
            saveReportUsersFilters({
                period: nextFilter.period,
                dates: [nextFilter.dates[0], nextFilter.dates[1]],
                customRange: nextFilter.customRange,
                viewMode: nextView,
                objectFilter: nextObjectFilter,
            });
        },
        [dateFilter, viewMode, objectFilter],
    );

    const handleDateFilterChange = (filter: ReportDateFilterState) => {
        setDateFilter(filter);
        setDates(filter.dates);
        persist({ dateFilter: filter });
    };

    const handleViewModeToggle = () => {
        const next: ReportUsersViewMode = viewMode === 'detail' ? 'summary' : 'detail';
        setViewMode(next);
        persist({ viewMode: next });
    };

    const handleObjectFilterChange = (next: ReportUsersObjectFilter) => {
        setObjectFilter(next);
        persist({ objectFilter: next });
    };

    useEffect(() => {
        const load = async () => {
            if (!fromMs || !toMs) return;
            setIsReporting(true);
            const resRep = await apiReport.get4Users(fromMs, toMs);
            if (!resRep) showToast(t('common:messError'), 'danger');
            setUsers(resRep?.users ?? []);
            setIsReporting(false);
        };
        void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch only when the period changes
    }, [fromMs, toMs]);

    const displayedUsers = useMemo(
        () => (users ? filterUsersByObject(users, objectFilter) : null),
        [users, objectFilter],
    );

    return {
        users: displayedUsers,
        isReporting,
        dates,
        setDates,
        dateFilter,
        viewMode,
        objectFilter,
        handleDateFilterChange,
        handleViewModeToggle,
        handleObjectFilterChange,
    };
};
